import { createContext, useContext, useEffect, useState, useCallback, ReactNode, createElement } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { exchangeAuth0Token } from '../api/auth';

interface AuthTokenContextValue {
  omsToken: string | null;
  isExchanging: boolean;
  error: string | null;
}

const AuthTokenContext = createContext<AuthTokenContextValue>({
  omsToken: null,
  isExchanging: true,
  error: null,
});

export function useAuthToken() {
  return useContext(AuthTokenContext);
}

export function AuthTokenProvider({ children }: { children: ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [omsToken, setOmsToken] = useState<string | null>(
    () => sessionStorage.getItem('oms_jwt'),
  );
  const [isExchanging, setIsExchanging] = useState(!omsToken);
  const [error, setError] = useState<string | null>(null);

  const exchangeToken = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setIsExchanging(true);
      setError(null);
      const auth0Token = await getAccessTokenSilently();
      const token = await exchangeAuth0Token(auth0Token);
      sessionStorage.setItem('oms_jwt', token);
      setOmsToken(token);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Token exchange failed';
      setError(message);
      console.error('Token exchange failed:', err);
    } finally {
      setIsExchanging(false);
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!omsToken && isAuthenticated) {
      exchangeToken();
    } else {
      setIsExchanging(false);
    }
  }, [omsToken, isAuthenticated, exchangeToken]);

  return createElement(
    AuthTokenContext.Provider,
    { value: { omsToken, isExchanging, error } },
    children,
  );
}
